import { usePluginEntitiesEnabledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/usePluginEntitiesEnabledVariation.js';
import { useCanInviteOrganizationMembers } from '#pplx/frontend/apps/ask/organization/hooks/useCanInviteOrganizationMembers.js';
import { useCanManageOrgPlugins } from '#pplx/frontend/apps/ask/organization/hooks/useCanManageOrgPlugins.js';
import { useCanManageOrgSkills } from '#pplx/frontend/apps/ask/organization/hooks/useCanManageOrgSkills.js';
import { useEffectivePermissions } from '#pplx/frontend/apps/ask/organization/hooks/useEffectivePermissions.js';
import { useEnterpriseApiSettingsPermissions } from '#pplx/frontend/apps/ask/organization/hooks/useEnterpriseApiSettingsPermissions.js';
import { useIsEnterpriseConnectorRbacEnabled } from '#pplx/frontend/apps/ask/organization/hooks/useIsEnterpriseConnectorRbacEnabled.js';
import { useIsOrgAdmin } from '#pplx/frontend/apps/ask/organization/hooks/useIsOrgAdmin.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { PAGE_GATE_AUTHORIZED, PAGE_GATE_LOADING, } from '#pplx/frontend/apps/ask/types/page-gate.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
// If whole section access is unauthorized, fallback to account landing page
const ORG_SECTION_UNAUTHORIZED = {
    type: 'unauthorized',
    redirectTo: '/account/details',
};
// If a page is unauthorized, fallback to the landing page
const ORG_PAGE_UNAUTHORIZED = {
    type: 'unauthorized',
    redirectTo: '/account/org/details',
};
const ORG_MEMBERS_NOT_ENTERPRISE_UNAUTHORIZED = {
    ...ORG_SECTION_UNAUTHORIZED,
    reason: 'not-enterprise',
};
const ORG_MEMBERS_CANNOT_INVITE_UNAUTHORIZED = {
    ...ORG_PAGE_UNAUTHORIZED,
    reason: 'cannot-invite-members',
};
export function useCanAccessOrgSection() {
    const isEnterpriseUser = useIsEnterpriseUser();
    // Redirect enterprise users to the org details page, and other users to
    // the account landing page.
    return isEnterpriseUser ? PAGE_GATE_AUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
function usePermissionPageGate(reason, ...anyOf) {
    const isEnterprise = useIsEnterpriseUser();
    const { permissions, isLoading } = useEffectivePermissions({ reason });
    if (isLoading) {
        return PAGE_GATE_LOADING;
    }
    if (anyOf.some((id) => permissions.has(id))) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
export function useAccessAccountOrgBillingPage() {
    return usePermissionPageGate('useAccessAccountOrgBillingPage', 'org_credit_limits_page_gate', 'org_billing_write');
}
export function useCanAccessOrgAdminPages() {
    return usePermissionPageGate('useCanAccessOrgAdminPages', 'org_settings_write');
}
export function useAccessAccountOrgDataPrivacyPage() {
    return useCanAccessOrgAdminPages();
}
/**
 * Gate for the admin Computer configuration page.
 *
 * Mirrors {@link useCanAccessOrgAdminPages} but keys on the product-specific
 * `org_computer_configure` permission instead of the generic
 * `org_settings_write`, so the catalog id has a real consumer (matching how
 * `org_comet_configure` gates the Comet admin page). The admin role grants
 * both ids, so admins are unaffected; a custom role can be scoped to
 * Computer configuration alone.
 */
export function useAccessAccountOrgComputerAdminPages() {
    return usePermissionPageGate('useAccessAccountOrgComputerAdminPages', 'org_computer_configure');
}
export function useAccessAccountOrgEnterpriseApisPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { canManageAnalyticsApi, canManageAuditLogApi, isLoading } = useEnterpriseApiSettingsPermissions();
    if (isLoading) {
        return PAGE_GATE_LOADING;
    }
    if (canManageAnalyticsApi || canManageAuditLogApi) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
/**
 * Gate for the org Skills admin page (`/account/org/skills`).
 *
 * Delegates to `useCanManageOrgSkills`, the single predicate mirroring
 * skillservice's write gate: legacy org-admin until the org's
 * `org-skill-entitlement-mode` is `enforce`, then the `org_skills_configure`
 * permission — so a custom role can be granted Skills-library management
 * without full org-settings access, and the page never renders for a caller
 * the backend would deny.
 */
export function useAccessAccountOrgSkillsPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { canManageOrgSkills, loading } = useCanManageOrgSkills('useAccessAccountOrgSkillsPage');
    // Block on the gate inputs (mode flag, then effective permissions in
    // enforce mode) so authorized users don't see an AUTHORIZED → LOADING →
    // AUTHORIZED flicker while they resolve.
    if (loading) {
        return PAGE_GATE_LOADING;
    }
    if (canManageOrgSkills) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
/**
 * Gate for the org Plugins admin page (`/account/org/plugins`).
 *
 * Flag first, permission second, because the flag decides whether the page
 * exists at all: `plugin-entities-enabled` gates every PluginService RPC, so
 * without it the list call is refused and an unflagged caller is redirected
 * rather than shown a page that cannot load.
 *
 * `useCanManageOrgPlugins` mirrors skillservice's plugin write gate: the
 * `org_skills_configure` permission decides, the same one that gates Skill
 * management, so the page never renders for a caller the backend would deny.
 */
export function useAccessAccountOrgPluginsPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { variation: pluginsEnabled, loading: pluginsLoading } = usePluginEntitiesEnabledVariation({
        defaultValue: false,
    });
    const { canManageOrgPlugins, loading: canManageLoading } = useCanManageOrgPlugins('useAccessAccountOrgPluginsPage');
    if (pluginsLoading) {
        return PAGE_GATE_LOADING;
    }
    if (!pluginsEnabled) {
        return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
    }
    // Block on the permission inputs so an authorized user never flickers
    // AUTHORIZED → LOADING → AUTHORIZED while they resolve.
    if (canManageLoading) {
        return PAGE_GATE_LOADING;
    }
    if (canManageOrgPlugins) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
export function useCanOpenOrgPluginSettings() {
    return useAccessAccountOrgPluginsPage().type === 'authorized';
}
export function useCanOpenOrgSkillSettings() {
    const canOpenPlugins = useCanOpenOrgPluginSettings();
    const skillsGate = useAccessAccountOrgSkillsPage();
    return canOpenPlugins && skillsGate.type === 'authorized';
}
/**
 * Gate for the org Connectors admin page (`/account/org/connectors`).
 *
 * Under RBAC the page is gated on the `org_connector_defaults_write`
 * permission (Connectors group) so a custom role can be granted connector
 * administration without full org-settings access. The backend already gates
 * the `connectors_enabled_by_default` toggle on this id; per-source toggles
 * follow the same org-scoped gate pending the Phase 5 resource-scoped ACL.
 * Off RBAC it falls back to the legacy org-admin check.
 */
export function useAccessAccountOrgConnectorsPage() {
    const isAdmin = useIsOrgAdmin();
    const isEnterprise = useIsEnterpriseUser();
    const { enabled: isRbacEnabled, loading: rbacLoading } = useIsEnterpriseConnectorRbacEnabled();
    const { permissions, isLoading: permsLoading } = useEffectivePermissions({
        reason: 'useAccessAccountOrgConnectorsPage',
        enabled: isRbacEnabled && !rbacLoading,
    });
    // Block on the rbac flag so admins don't see an AUTHORIZED → LOADING → AUTHORIZED
    // flicker when the flag resolves from its default `false` to `true`.
    if (rbacLoading) {
        return PAGE_GATE_LOADING;
    }
    if (isRbacEnabled) {
        if (permsLoading) {
            return PAGE_GATE_LOADING;
        }
        if (permissions.has('org_connector_defaults_write')) {
            return PAGE_GATE_AUTHORIZED;
        }
        return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
    }
    if (isAdmin) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
export function useCanOpenOrgConnectorSettings() {
    return useAccessAccountOrgConnectorsPage().type === 'authorized';
}
/**
 * Gate for the organization credential vault (`/account/org/credentials-vault`).
 *
 * Requires the independently delegated `org_custom_credentials_write` permission.
 */
export function useAccessAccountOrgCredentialsVaultPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { permissions, organizationCredentialVaultRoleAuthorized, isLoading: permsLoading, } = useEffectivePermissions({ reason: 'useAccessAccountOrgCredentialsVaultPage' });
    if (permsLoading) {
        return PAGE_GATE_LOADING;
    }
    const canAccessVault = organizationCredentialVaultRoleAuthorized ?? permissions.has('org_custom_credentials_write');
    if (canAccessVault) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise ? ORG_PAGE_UNAUTHORIZED : ORG_SECTION_UNAUTHORIZED;
}
export function useAccessAccountOrgCreditLimitsPage() {
    return usePermissionPageGate('useAccessAccountOrgCreditLimitsPage', 'org_credit_limits_page_gate');
}
export function useAccessAccountOrgCometMvpAdminPages() {
    const isEnterprise = useIsEnterpriseUser();
    const { permissions, isLoading } = useEffectivePermissions({
        reason: 'useAccessAccountOrgCometMvpAdminPages',
    });
    if (!isEnterprise) {
        return ORG_SECTION_UNAUTHORIZED;
    }
    if (isLoading) {
        return PAGE_GATE_LOADING;
    }
    return permissions.has('org_comet_configure') ? PAGE_GATE_AUTHORIZED : ORG_PAGE_UNAUTHORIZED;
}
export function useAccessOrgEncryptionPage() {
    // Session-derived gates read `false` until the session hydrates; hold LOADING
    // so a cold/direct load doesn't emit a premature redirect on first paint.
    const { status: sessionStatus } = useSession();
    const isEnterprise = useIsEnterpriseUser();
    const userOrgData = useUserOrgData({ reason: 'useAccessOrgEncryptionPage' });
    const { permissions, isLoading: permsLoading, isError: permsError, isLoaded: permsLoaded, } = useEffectivePermissions({ reason: 'useAccessOrgEncryptionPage' });
    if (sessionStatus === 'loading') {
        return PAGE_GATE_LOADING;
    }
    if (!isEnterprise) {
        return ORG_SECTION_UNAUTHORIZED;
    }
    if (permsLoading || (!permsLoaded && !permsError)) {
        return PAGE_GATE_LOADING;
    }
    // BYOK is gated on its own `org_encryption_page_access` permission (catalog
    // entry is `ui_hidden`, seeded only on the `admin` role).
    if (!permissions.has('org_encryption_page_access')) {
        return ORG_PAGE_UNAUTHORIZED;
    }
    if (userOrgData.isLoading) {
        return PAGE_GATE_LOADING;
    }
    return userOrgData.organization?.settings?.byok_admin_enabled
        ? PAGE_GATE_AUTHORIZED
        : ORG_PAGE_UNAUTHORIZED;
}
export function useAccessAccountOrgRolesPermissionsPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { permissions, isLoading } = useEffectivePermissions({
        reason: 'useAccessAccountOrgRolesPermissionsPage',
    });
    if (!isEnterprise) {
        return ORG_SECTION_UNAUTHORIZED;
    }
    if (isLoading) {
        return PAGE_GATE_LOADING;
    }
    return permissions.has('org_admin_grant') ? PAGE_GATE_AUTHORIZED : ORG_PAGE_UNAUTHORIZED;
}
export function useAccessAccountOrgMembersPage() {
    const isEnterprise = useIsEnterpriseUser();
    const { canInviteMembers, loading } = useCanInviteOrganizationMembers();
    if (loading) {
        return PAGE_GATE_LOADING;
    }
    if (canInviteMembers) {
        return PAGE_GATE_AUTHORIZED;
    }
    return isEnterprise
        ? ORG_MEMBERS_CANNOT_INVITE_UNAUTHORIZED
        : ORG_MEMBERS_NOT_ENTERPRISE_UNAUTHORIZED;
}
export function useAccessAccountOrgInsightsPage() {
    const isEnterpriseUser = useIsEnterpriseUser();
    const { isEnterprise: hasSessionEnterpriseSubscription } = useIsSubscribed();
    const userOrgData = useUserOrgData({
        reason: 'useAccessAccountOrgInsightsPage',
    });
    const hasActiveEnterpriseSubscription = userOrgData.organization?.subscription_is_on ?? hasSessionEnterpriseSubscription;
    const isOrgSubscriptionLoading = !hasSessionEnterpriseSubscription &&
        userOrgData.organization?.subscription_is_on === undefined &&
        userOrgData.isLoading;
    const { permissions, isLoading: permsLoading } = useEffectivePermissions({
        reason: 'useAccessAccountOrgInsightsPage',
    });
    if (!isEnterpriseUser) {
        return ORG_SECTION_UNAUTHORIZED;
    }
    if (permsLoading) {
        return PAGE_GATE_LOADING;
    }
    if (!permissions.has('org_insights_page_gate')) {
        return ORG_PAGE_UNAUTHORIZED;
    }
    if (isOrgSubscriptionLoading) {
        return PAGE_GATE_LOADING;
    }
    return hasActiveEnterpriseSubscription ? PAGE_GATE_AUTHORIZED : ORG_PAGE_UNAUTHORIZED;
}
//# sourceMappingURL=access.js.map